import {
  AssetCategory,
  AssetDetailsSortBy,
  AssetPageTab,
  SortDirection,
} from '@spectral/types'
import pull from 'lodash/pull'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import uniqBy from 'lodash/uniqBy'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { IssueKind, IssueStatus } from '@spectral/types/issue'
import { castArray } from 'lodash'
import IacFilesView from '../../blocks/asset-page/iac/iac-view/view'
import { toElementId } from '../../blocks/asset-page/utils'
import assetPageTracker from '../../common/track/asset-page'
import { scrollToElementWithId } from '../../common/utils'
import useExportToFile from '../../hooks/useExportToFile'
import useIssuesActionIntegrations from '../../hooks/useIssuesActionIntegrations'
import { useIssuesTableActions } from '../../hooks/useIssuesTableActions'
import useListPageManagement from '../../hooks/useListPageManagement'
import useQueryStringState from '../../hooks/useQueryStringState'
import { Dispatch, select } from '../../redux/store'
import { SubPage } from '../../components/tab/sub-page'

export const iacListInitialParams = {
  page: 1,
  pageSize: 10,
  sortBy: AssetDetailsSortBy.COUNT,
  sortDirection: SortDirection.DESC,
  tab: AssetPageTab.Iac,
  status: IssueStatus.Active,
}

const filtersKeys = [
  'tab',
  'path',
  'severity',
  'status',
  'assignees',
  'detectorId',
  'issueId',
  'resources',
  'tag',
]

const iacListInitialFiltersParams = pick(iacListInitialParams, filtersKeys)

const IacList = ({ fetchAsset, asset }) => {
  const dispatch: Dispatch = useDispatch()
  const { assetId } = useParams()
  const [
    currentParamsValues,
    saveParamValueInQueryString,
  ] = useQueryStringState(['expandedFile', 'issueId'])
  const { expandedFile } = currentParamsValues

  const { iacFiles, pagination, status, filtersData } = useSelector(
    select.AssetPage.iacFilesList
  )
  const isDownloadingExport = useSelector(select.AssetPage.isDownloadingExport)
  const {
    status: { isLoadingAssignableMembers, isAssignableMembersLoaded },
    assignableMembers,
  } = useSelector(select.AssetPage.assetPage)
  const { loaded, isRefreshing, isRefreshingIacFileDetails } = status

  const { integrations } = useIssuesActionIntegrations()
  const [selectedIssues, setSelectedIssues] = useState([])
  const onIssueSelectionChanged = (issuesToChange, isSelected) => {
    const updatedSelectedIssues = isSelected
      ? uniqBy([...selectedIssues, ...issuesToChange], 'pid')
      : pull(selectedIssues, ...issuesToChange)
    setSelectedIssues([...updatedSelectedIssues])
  }
  const resetTableState = () => {
    setSelectedIssues([])
  }
  const [
    currentQueryStringParams,
    setQueryStringParam,
    clearQueryStringParam,
    onPaginationChange,
    trackFilter,
    trackSort,
    fetchData,
  ] = useListPageManagement(
    filtersKeys,
    iacListInitialParams,
    dispatch.AssetPage.fetchIacFiles,
    dispatch.AssetPage.resetIacFiles,
    assetPageTracker.filter.bind(assetPageTracker),
    assetPageTracker.sort.bind(assetPageTracker),
    assetPageTracker.pagination.bind(assetPageTracker),
    { assetId: decodeURIComponent(assetId) }
  )

  const [isDataFiltered, setIsDataFiltered] = useState(false)
  useEffect(() => {
    const currentParamsValuesPick = pick(currentQueryStringParams, filtersKeys)
    setIsDataFiltered(
      !isEqual(currentParamsValuesPick, iacListInitialFiltersParams)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentQueryStringParams)])

  const [exportToCsv] = useExportToFile(
    () => assetPageTracker.export.bind(assetPageTracker)('csv', true),
    () =>
      dispatch.AssetPage.exportIssues({
        ...currentQueryStringParams,
        category: AssetCategory.Code,
        assetId: [assetId],
        issueKinds: castArray(IssueKind.Iac),
        output: 'csv',
      }),
    'csv',
    currentQueryStringParams
  )

  const [exportToPdf] = useExportToFile(
    () => assetPageTracker.export.bind(assetPageTracker)('pdf', true),
    () =>
      dispatch.AssetPage.exportIssues({
        ...currentQueryStringParams,
        category: AssetCategory.Code,
        assetId: [assetId],
        issueKinds: castArray(IssueKind.Iac),
        output: 'pdf',
      }),
    'pdf',
    currentQueryStringParams
  )

  const exportActions = useMemo(() => {
    return {
      csv: { active: true, onExport: exportToCsv },
      pdf: { active: true, onExport: exportToPdf },
    }
  }, [exportToCsv, exportToPdf])

  const refetchAllData = useCallback(async () => {
    await fetchAsset()
    fetchData()
    if (expandedFile) {
      await dispatch.AssetPage.fetchIacFilesDetails({
        ...currentQueryStringParams,
        iacFiles: [expandedFile],
        assetId,
      })
    }

    resetTableState()
  }, [
    assetId,
    currentQueryStringParams,
    dispatch.AssetPage,
    expandedFile,
    fetchAsset,
    fetchData,
  ])

  const handlers = useIssuesTableActions('asset_page', {
    ignore: refetchAllData,
    snooze: refetchAllData,
    resolve: refetchAllData,
    unResolve: refetchAllData,
    assigneeChange: refetchAllData,
    assigneeRemove: refetchAllData,
    severityChange: () => {
      fetchData({ updateOnlyFilters: true })
    },
  })

  const onChangeFileExpansion = (updatedExpandedFile) => {
    saveParamValueInQueryString({ expandedFile: updatedExpandedFile }, true)
    if (updatedExpandedFile) {
      dispatch.AssetPage.fetchIacFilesDetails({
        assetId,
        iacFiles: updatedExpandedFile,
        ...currentQueryStringParams,
      })
      assetPageTracker.iacFileClicked()
    }
  }

  useEffect(() => {
    if (expandedFile && loaded) {
      dispatch.AssetPage.fetchIacFilesDetails({
        assetId,
        iacFiles: [expandedFile],
        ...currentQueryStringParams,
      })
      setTimeout(() => {
        scrollToElementWithId(toElementId(expandedFile))
      }, 0)
    }
  }, [loaded])

  return (
    <SubPage isLoading={!loaded} name="IaC" sx={{ height: '100%' }}>
      <IacFilesView
        asset={asset}
        setQueryStringParam={setQueryStringParam}
        clearQueryStringParam={clearQueryStringParam}
        currentQueryStringParams={currentQueryStringParams}
        trackFilter={trackFilter}
        trackSort={trackSort}
        expandedFile={expandedFile}
        loaded={loaded}
        onPaginationChange={onPaginationChange}
        isRefreshing={isRefreshing}
        isRefreshingIacFileDetails={isRefreshingIacFileDetails}
        iacFiles={iacFiles}
        onChangeFileExpansion={onChangeFileExpansion}
        pagination={pagination}
        actionHandlers={handlers}
        onIssueSelectionChanged={onIssueSelectionChanged}
        selectedIssues={selectedIssues}
        isDownloadingExport={isDownloadingExport}
        exportActions={exportActions}
        integrations={integrations}
        onActionIntegrationSubmit={refetchAllData}
        filtersData={filtersData}
        assignableMembers={assignableMembers}
        isLoadingAssignableMembers={isLoadingAssignableMembers}
        isAssignableMembersLoaded={isAssignableMembersLoaded}
        isDataFiltered={isDataFiltered}
      />
    </SubPage>
  )
}

export default IacList
